import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable} from "mobx";
import type {IPlayerProfileSessionsProps} from "./player_profile_sessions.component";
import type {IRoundsStore, ISession} from "data/stores/rounds/rounds.store";
import {eq, get} from "lodash";
import type {IFantasyData, IPlayersStore} from "data/stores/players/players.store";
import {IPlayerProfileStats} from "views/components/player_profile_stats/player_profile_stats.component";
import {ifNoValueShowDash} from "data/utils";
import {getPts} from "data/utils/getPts";
import {RoundStatus} from "data/enums";

export interface IPlayerProfileSessionsController
	extends ViewController<IPlayerProfileSessionsProps> {
	get i18n(): ILocalizationStore;
	get selectedRound(): ISession | null;
	get hasPrevRound(): ISession | undefined;
	get hasNextRound(): ISession | undefined;
	get pickedBy(): number;
	get points(): number;
	get statsByHoleByShot(): IPlayerProfileStats[];
	get bonusPointsStats(): IPlayerProfileStats[];

	prevRound: () => void;
	nextRound: () => void;
}

@injectable()
export class PlayerProfileSessionsController implements IPlayerProfileSessionsController {
	@observable private _playerId?: number;
	@observable _selectedRoundId: number = this.rounds[0].id;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.PlayersStore) private _playersStore: IPlayersStore
	) {
		makeAutoObservable(this);
	}

	get rounds() {
		return this._roundStore.rounds;
	}

	get selectedRound() {
		if (!this._selectedRoundId) {
			return null;
		}

		return this._roundStore.getRoundById(this._selectedRoundId);
	}

	get player() {
		if (!this._playerId) {
			return null;
		}

		return this._playersStore.getPlayerById(this._playerId);
	}

	get pickedBy() {
		return get(this.player?.stats.fantasyPickedBy, this._selectedRoundId, 0);
	}

	get points() {
		return get(this.player?.fantasy, `${this._selectedRoundId}.points`, 0);
	}

	get hasPrevRound() {
		const index = this.rounds.findIndex((round) => eq(round.id, this._selectedRoundId));
		return this.rounds[index - 1];
	}

	get hasNextRound() {
		const index = this.rounds.findIndex((round) => eq(round.id, this._selectedRoundId));
		return this.rounds[index + 1];
	}

	get fantasyStats() {
		return get(this.player?.fantasy, this._selectedRoundId, {} as IFantasyData);
	}

	get isScheduledOrPlaying() {
		return [RoundStatus.Scheduled, RoundStatus.Playing].includes(
			this.selectedRound?.status || RoundStatus.Scheduled
		);
	}

	get statsByHoleByShot() {
		return [
			{
				value: ifNoValueShowDash(get(this.fantasyStats, "holes_win", 0)),
				label: this.i18n.t("player_profile.session.holes_won", "Holes Won"),
				pts: getPts(this.fantasyStats, "holes_win"),
			},
			{
				value: ifNoValueShowDash(get(this.fantasyStats, "holes_halved", 0)),
				label: this.i18n.t("player_profile.session.holes_halved", "Holes Halved"),
				pts: getPts(this.fantasyStats, "holes_halved"),
			},
			{
				value: ifNoValueShowDash(get(this.fantasyStats, "holes_lost", 0)),
				label: this.i18n.t("player_profile.session.holes_lost", "Holes Lost"),
				pts: getPts(this.fantasyStats, "holes_lost"),
			},
			{
				value: ifNoValueShowDash(get(this.fantasyStats, "fairways_hit", 0)),
				label: this.i18n.t("player_profile.session.fairways_hit", "Fairways Hit"),
				pts: getPts(this.fantasyStats, "fairways_hit"),
			},
			{
				value: ifNoValueShowDash(get(this.fantasyStats, "green_in_reg", 0)),
				label: this.i18n.t("player_profile.session.green_in_reg", "Green in Reg"),
				pts: getPts(this.fantasyStats, "green_in_reg"),
			},
			{
				value: ifNoValueShowDash(get(this.fantasyStats, "hole_in_one", 0)),
				label: this.i18n.t("player_profile.session.hole_in_one", "Hole-in-One"),
				pts: getPts(this.fantasyStats, "hole_in_one"),
			},
		];
	}

	get getMatchResult() {
		const winMatchValue = get(this.fantasyStats, "win_match", 0);
		const tieMatchValue = get(this.fantasyStats, "tie_match", 0);

		const isWin = winMatchValue > 0;
		const isTie = tieMatchValue > 0;

		const {value, label, field, className, valueInCircle} = isTie
			? {
					value: tieMatchValue,
					className: "tied",
					valueInCircle: true,
					label: this.i18n.t("player_profile.session.tie", "T"),
					field: "tie_match" as const,
			  }
			: isWin
			? {
					value: winMatchValue,
					className: "win",
					valueInCircle: true,
					label: this.i18n.t("player_profile.session.win", "W"),
					field: "win_match" as const,
			  }
			: {
					value: 0,
					className: "lose",
					valueInCircle: !this.isScheduledOrPlaying,
					label: this.isScheduledOrPlaying
						? "-"
						: this.i18n.t("player_profile.session.loss", "L"),
					field: null,
			  };

		return {
			value,
			className,
			valueInCircle,
			label,
			field,
		};
	}

	get bonusPointsStats() {
		const noHolesLostValue = get(this.fantasyStats, "no_holes_lost", 0);

		return [
			{
				value: ifNoValueShowDash(get(this.fantasyStats, "holes_3_row", 0)),
				label: this.i18n.t("player_profile.session.holes_3_row", "3 Holes Won in a Row"),
				pts: getPts(this.fantasyStats, "holes_3_row"),
			},
			{
				value: ifNoValueShowDash(noHolesLostValue),
				checkInstedValue: Boolean(noHolesLostValue),
				label: this.i18n.t(
					"player_profile.session.no_holes_lost",
					"No Holes Lost in Match"
				),
				pts: getPts(this.fantasyStats, "no_holes_lost"),
			},
			{
				value: this.getMatchResult.label,
				valueInCircle: this.getMatchResult.valueInCircle,
				valueInCircleClassName: this.getMatchResult.className,
				label: this.i18n.t("player_profile.session.match_result", "Match Result"),
				pts: getPts(this.fantasyStats, this.getMatchResult.field),
			},
			{
				value: ifNoValueShowDash(get(this.fantasyStats, "unplayed_holes", 0)),
				label: this.i18n.t("player_profile.session.unplayed_holes", "Unplayed Holes"),
				pts: getPts(this.fantasyStats, "unplayed_holes"),
			},
		];
	}

	@action prevRound = () => {
		if (this.hasPrevRound) {
			this._selectedRoundId = this.hasPrevRound.id;
		}
	};

	@action nextRound = () => {
		if (this.hasNextRound) {
			this._selectedRoundId = this.hasNextRound.id;
		}
	};

	@action init({playerId}: IPlayerProfileSessionsProps) {
		this._playerId = playerId;
	}
}
